import {
    CoreActivityItemModel,
    CoreActivityOutcomeTypeEnumDesc,
    CoreActivityEvidenceAvailableTypeEnumDesc,
    ActivityConductedByTypeEnumDesc,
    AFIActivitiesPerformedByOptionsEnumDesc,
} from '../../../api/models';
import { NestedListDetailsProp } from '../../../components/wizardLayout/wizardNestedListContent/types';
import { FieldGroupModel, FieldModel } from '../../../components/form/pageForm/types';
import { countrySelectInputProps, yesNoRadioButtonProps, yesRspYesCrcNoRadioButtonProps } from '../../../FormDefinitions/CommonComponentProps';
import { CheckboxGroupProps } from '../../../components/input/checkboxGroup/types';

// Q.61
const isToListPreviousCoreActivity: FieldModel<CoreActivityItemModel> = yesNoRadioButtonProps(
    'isToListPreviousCoreActivity',
    'Is it your intention to list a previously registered core R&D activity for this application?',
    [],
);

// Q.62
const name: FieldModel<CoreActivityItemModel> = {
    component: 'textInput',
    label: 'Name for this core R&D activity',
    name: 'name',
    contentKey: ['CoreActivityName'],
    // maxLength: 250,  // BR3.2_Max length - permit entry of data past maximum and display invalid data error
};

// Q.63
const describeCoreActivity: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Describe the core R&D activity',
    contentKey: ['AFIDescribeCoreActivity'],
    name: 'describeCoreActivity',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.64
const projectId: FieldModel<CoreActivityItemModel> = {
    component: 'selectInput',
    label: 'Which project is this core R&D activity related to?',
    name: 'projectId',
    componentProps: {
        id: 'projectId',
        toggleName: 'Please choose...',
    },
    contentKey: ['CoreActivityProjectDescription'],
};

// Q.65
const isActivityExcludedFromCore: FieldModel<CoreActivityItemModel> = {
    component: 'radiobuttonGroup',
    label: 'Does this activity fall into one of the categories that exclude it from being a core R&D activity?',
    name: 'isActivityExcludedFromCore',
    contentKey: ['CoreActivitiesActivityExcludedFromCore'],
    componentProps: {
        name: 'isActivityExcludedFromCore',
        radioOptions: [
            {
                id: 'isActivityExcludedFromCore-Yes',
                label: 'Yes',
                value: 'Yes',
                tooltip: 'Yes',
            },
            {
                id: 'isActivityExcludedFromCore-No',
                label: 'No',
                value: 'No',
                tooltip: 'No',
            },
        ],
        contentClassname: 'nestedListLatinRoman',
    },
};

// Q.66
const activitiesPerformedBy: FieldModel<CoreActivityItemModel> = {
    component: 'radiobuttonGroup',
    label: 'Who was this core R&D activity performed by?',
    name: 'activitiesPerformedBy',
    contentKey: ['AOFActivitiesPerformedBy', 'AOFWhatIsARSP', 'AOFWhatIsACRC'],
    componentProps: {
        name: 'activitiesPerformedBy',
        radioOptions: [
            {
                id: 'activitiesPerformedBy-OnlyTheRnDCompany',
                label: AFIActivitiesPerformedByOptionsEnumDesc['OnlyTheRnDCompany'],
                value: 'OnlyTheRnDCompany',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['OnlyTheRnDCompany'],
                maxWidth: '812px',
            },
            {
                id: 'activitiesPerformedBy-RnDCompanyAndOthers',
                label: AFIActivitiesPerformedByOptionsEnumDesc['RnDCompanyAndOthers'],
                value: 'RnDCompanyAndOthers',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['RnDCompanyAndOthers'],
                maxWidth: '812px',
            },
            {
                id: 'activitiesPerformedBy-EitherASubsidiary',
                label: AFIActivitiesPerformedByOptionsEnumDesc['EitherASubsidiary'],
                value: 'EitherASubsidiary',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['EitherASubsidiary'],
                maxWidth: '812px',
            },
            {
                id: 'activitiesPerformedBy-OnlyOthers',
                label: AFIActivitiesPerformedByOptionsEnumDesc['OnlyOthers'],
                value: 'OnlyOthers',
                tooltip: AFIActivitiesPerformedByOptionsEnumDesc['OnlyOthers'],
                maxWidth: '812px',
            },
        ],
    },
};

// Q.67
const companyName: FieldModel<CoreActivityItemModel> = {
    component: 'textInput',
    label: 'Name of company performing this core R&D activity',
    name: 'companyName',
    // maxLength: 250,  // BR3.2_Max length - permit entry of data past maximum and display invalid data error
};

// Q.68
const isEntityConductingActivityConnectedOrAffiliated = yesNoRadioButtonProps('isEntityConductingActivityConnectedOrAffiliated',
    'Is the entity who will conduct this core R&D activity a connected or affiliated entity?', []);

// Q.69
const isActivityRnDEntityLocatedOutsideAustralia = yesNoRadioButtonProps('isActivityRnDEntityLocatedOutsideAustralia',
    // tslint:disable-next-line:max-line-length
    'Is the core R&D activity being conducted under an agreement the R&D entity has with a connected or affiliated entity who is located outside of Australia?', []);

// Q.70
const activityConductedBy: FieldModel<CoreActivityItemModel> = {
    component: 'radiobuttonGroup',
    label: 'Will you conduct this core R&D activity as the head entity of a consolidated group or MEC group, or will this activity be conducted by a subsidiary?',
    name: 'activityConductedBy',
    componentProps: {
        name: 'activityConductedBy',
        radioOptions: [
            {
                id: 'activityConductedBy-mecGroup',
                label: ActivityConductedByTypeEnumDesc['ConsolidatedMECGroup'],
                value: 'ConsolidatedMECGroup',
                tooltip: ActivityConductedByTypeEnumDesc['ConsolidatedMECGroup'],
                maxWidth: '812px',
            },
            {
                id: 'activityConductedBy-subsidiary',
                label: ActivityConductedByTypeEnumDesc['Subsidiary'],
                value: 'Subsidiary',
                tooltip: ActivityConductedByTypeEnumDesc['Subsidiary'],
                maxWidth: '812px',
            },
        ],
    },
};

// Q.71, Q.71.1 - Q.71.5
const subsidiaryConductAbn: FieldModel<CoreActivityItemModel> = {
    component: 'abnAcnLookup',
    label: `What is the ABN, ACN or ARBN of the subsidiary conducting this core R&D activity?`,
    name: 'subsidiaryConductAbn',
};

// Q.72
const companyCountryOfResidence = countrySelectInputProps(
    'companyCountryOfResidence',
    'Country of residence for company performing this core R&D activity?',
    ['CompanyCountryOfResidence']);

// Q.73
const futureCoreActivity: FieldModel<CoreActivityItemModel> = yesNoRadioButtonProps(
    'futureCoreActivity',
    'Does this core R&D activity commence after the end of your income period for this application?',
    ['FutureCoreActivityDescription'],
);

// Q.74, Q.74.1 - Q.74.4
const duration: FieldModel<CoreActivityItemModel> = {
    component: 'monthYearRangeInput',
    label: 'Enter the actual or anticipated start and end dates for this core R&D activity',
    name: 'duration',
    contentKey: ['CoreActivityDates'],
};

// Q.75
const otherOrganisation: FieldModel<CoreActivityItemModel> = yesRspYesCrcNoRadioButtonProps(
    'otherOrganisation',
    'Was some or all of this core R&D activity conducted by a Research Service Provider or Cooperative Research Centre?',
    ['CoreActivityConductedByCrcOrRsp']);

// Q.76
const isActivityCoveredByIRDD = yesNoRadioButtonProps('isActivityCoveredByIRDD',
    'Is this core R&D activity covered by an Industry Research and Development Determination?', ['AOFActivityCoveredByIRDD']);

// Q.77
const determinationRelyingOn: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Which determination are you relying on?',
    contentKey: ['AFIDeterminationRelyingOn'],
    name: 'determinationRelyingOn',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.78
const outcomeCheckboxGroupProps: CheckboxGroupProps = {
    contentKey: ['CoreActivityOutcome'],
    checkOptions: [
        {
            id: 'outcome-no-information',
            label: CoreActivityOutcomeTypeEnumDesc['NoInformation'],
            name: 'outcome-no-information',
            value: 'NoInformation',
        },
        {
            id: 'outcome-no-solution',
            label: CoreActivityOutcomeTypeEnumDesc['NoSolution'],
            name: 'outcome-no-solution',
            value: 'NoSolution',
        },
        {
            id: 'outcome-cannot-adapt',
            label: CoreActivityOutcomeTypeEnumDesc['CannotAdapt'],
            name: 'outcome-cannot-adapt',
            value: 'CannotAdapt',
        },
        {
            id: 'outcome-other',
            label: CoreActivityOutcomeTypeEnumDesc['Other'],
            name: 'outcome-other',
            value: 'Other',
        },
    ],
    includeAll: false,
    includeNone: true,
    includeNoneLabel: CoreActivityOutcomeTypeEnumDesc['None'],
    id: 'outcome',
    label: 'How did the company determine that the outcome could not be known in advance?',
};
// Q.78 main stuff
const outcome: FieldModel<CoreActivityItemModel> = {
    component: 'checkboxGroup',
    label: '',
    componentProps: outcomeCheckboxGroupProps,
    name: 'outcome',
    contentKey: ['CoreActivityOutcome'],
};

// Q.79
const sourcesInvestigated: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Please explain what sources were investigated and what information was found.',
    contentKey: ['CoreActivitySourcesInvestigated2'],
    name: 'sourcesInvestigated',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.80
const competentProfesionalReason: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Why couldn’t a competent professional have known or determined the outcome in advance?',
    contentKey: ['CoreActivityCompetentProfessionalDefinition2'],
    name: 'competentProfesionalReason',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.81
const whyNoInvestigation: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'Please explain why the company did not search existing knowledge.',
    contentKey: ['CoreActivityWhyNoInvestigation'],
    name: 'whyNoInvestigation',
    componentProps: {
        maxCharacters: 1000,
    },
};

// Q.82
const hypothesis: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'What is the hypothesis?',
    contentKey: ['CoreActivityHypothesis', 'CoreActivityHowToDescribeHypothesis'],
    name: 'hypothesis',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.83
const experiment: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'What is the experiment and how will it / did it test the hypothesis?',
    contentKey: ['CoreActivityExperiment', 'CoreActivityExperimentExplainWhatAndWhy'],
    name: 'experiment',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.84
const evaluation: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'How did you evaluate or plan to evaluate results from your experiment?',
    contentKey: ['CoreActivityEvaluation2'],
    name: 'evaluation',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.85
const conclusions: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'If you reached conclusions from your experiments in the selected income period, describe those conclusions.',
    contentKey: ['CoreActivityExperimentConclusions', 'CoreActivityConclusions'],
    name: 'conclusions',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.86 - checkboxes
const evidenceAvailableCheckboxGroupProps: CheckboxGroupProps = {
    contentKey: ['CoreActivityAvailableEvidence'],
    checkOptions: [
        {
            id: 'evidence-design-chk',
            label: CoreActivityEvidenceAvailableTypeEnumDesc['Design'],
            name: 'Design',
            value: 'Design',
        },
        {
            id: 'evidence-evaluation-chk',
            label: CoreActivityEvidenceAvailableTypeEnumDesc['Evaluation'],
            name: 'Evaluation',
            value: 'Evaluation',
        },
        {
            id: 'evidence-revision-chk',
            label: CoreActivityEvidenceAvailableTypeEnumDesc['Revision'],
            name: 'Revision',
            value: 'Revision',
        },
        {
            id: 'evidence-existing-chk',
            label: CoreActivityEvidenceAvailableTypeEnumDesc['Existing'],
            name: 'Existing',
            value: 'Existing',
        },
        {
            id: 'evidence-generate-chk',
            label: CoreActivityEvidenceAvailableTypeEnumDesc['Generate'],
            name: 'Generate',
            value: 'Generate',
        },
        {
            id: 'evidence-other-chk',
            label: CoreActivityEvidenceAvailableTypeEnumDesc['Other'],
            name: 'Other',
            value: 'Other',
        },
    ],
    includeAll: false,
    includeNone: true,
    includeNoneLabel: CoreActivityEvidenceAvailableTypeEnumDesc['None'],
    id: 'evidenceAvailable',
    label: 'What evidence did the company keep about this core R&D activity?',
};

// Q.86 - main stuff
const evidenceAvailable: FieldModel<CoreActivityItemModel> = {
    component: 'checkboxGroup',
    label: '',
    componentProps: evidenceAvailableCheckboxGroupProps,
    name: 'evidenceAvailable',
    contentKey: ['CoreActivityAvailableEvidence'],
};

// Q.87
const evidenceAvailableOther: FieldModel<CoreActivityItemModel> = {
    component: 'textInput',
    label: 'Please describe the other evidence.',
    name: 'evidenceAvailableOther',
    contentKey: ['CoreActivityOtherEvidence'],
    // maxLength: 100,  //  BR37_ Paste over maximum
};

// Q.88
const purpose: FieldModel<CoreActivityItemModel> = yesNoRadioButtonProps(
    'purpose',
    'Did you conduct this core R&D activity for a purpose of generating new knowledge?',
    ['CoreActivityNewKnowledgeDefinition']);

// Q.89
const newKnowledge: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'What new knowledge was / will this core R&D activity (be) intended to produce?',
    contentKey: ['CoreActivityNewKnowledgeDescription'],
    name: 'newKnowledge',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q.90 or Q.91
const otherOrganisationName: FieldModel<CoreActivityItemModel> = {
    component: 'selectInput',
    label: 'Which CRC or RSP conducted some or all of this activity?',
    name: 'otherOrganisationName',
    componentProps: {
        toggleName: 'Please choose...',
    },
    contentKey: ['CoreActivityConductedByRspName'],
};

// Q.90.1 or Q.91.1
const briefDescription: FieldModel<CoreActivityItemModel> = {
    component: 'textAreaInput',
    label: 'A brief description of the activity, services provided by the RSP/CRC and what new knowledge the activity was intended to create',
    name: 'briefDescription',
    contentKey: ['CoreActivityBriefDescription'],
    componentProps: {
        maxCharacters: 2000,
    },
};

// Q.92
const expenditure: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'For your selected income period, what was the estimated expenditure for this core R&D activity?',
    contentKey: ['CoreActivityExpenditure'],
    name: 'expenditure',
    maxWidth: '352px',
    // Allowing 15 digits plus 4 comma separators as max input length
    maxLength: 19,
};

// Q. 93.1
const cellWidth = '22rem';
const expenditurePriorToYear1: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'Expenditure incurred prior to [YYYY/YY]',
    name: 'expenditurePriorToYear1',
    maxWidth: cellWidth,
    maxLength: 15,
};

// Q. 93.2
const financialYear1EstimatedExpenditure: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'Anticipated expenditure income year [YYYY/YY]',
    name: 'financialYear1EstimatedExpenditure',
    maxWidth: cellWidth,
    maxLength: 15,
};

// Q. 93.3
const financialYear2EstimatedExpenditure: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'Anticipated expenditure income year [YYYY/YY+1]',
    name: 'financialYear2EstimatedExpenditure',
    maxWidth: cellWidth,
    maxLength: 15,
};

// Q. 93.4
const financialYear3EstimatedExpenditure: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'Anticipated expenditure income year [YYYY/YY+2]',
    name: 'financialYear3EstimatedExpenditure',
    maxWidth: cellWidth,
    maxLength: 15,
};

// Q. 93.5
const financialYear3EstimatedExpenditurePost: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'Anticipated expenditure post [YYYY/YY+2]',
    name: 'financialYear3EstimatedExpenditurePost',
    maxWidth: cellWidth,
    maxLength: 15,
};

// Q. 93.6
const totalExpenditure: FieldModel<CoreActivityItemModel> = {
    component: 'currencyInput',
    label: 'Total expenditure',
    name: 'totalExpenditure',
    maxWidth: cellWidth,
    maxLength: 15,
    componentProps: {
        readOnly: true,
    },
};

// Q. 93 heading
const totalAnticipatedExpendituresHeader: FieldModel<CoreActivityItemModel> = {
    component: 'heading',
    label: 'What is the total actual and reasonably anticipated expenditure for the applicant entity on this activity (per income year)?',
    name: 'totalAnticipatedExpendituresHeader',
    contentKey: ['AFITotalAnticipatedExpenditure'],
    componentProps: {
        className: 'rdtiQuestion',
        tag: 'label',
    },
};

// Q. 93 grouping the stuff
const totalAnticipatedExpenditure: FieldGroupModel<CoreActivityItemModel> = {
    label: '',
    name: 'totalAnticipatedExpenditure',
    displayInRow: false,
    showOneErrorFieldOnly: false,
    showHelpContextOnTop: true,
    showGroupHeadingLabelInPdf: true,
    groupFields:
    {
        expenditurePriorToYear1,
        financialYear1EstimatedExpenditure,
        financialYear2EstimatedExpenditure,
        financialYear3EstimatedExpenditure,
        financialYear3EstimatedExpenditurePost,
        totalExpenditure,
    },
};

const coreActivities: NestedListDetailsProp<CoreActivityItemModel> = {
    apiEndpoint: '/core-activities',
    name: 'coreActivities',
    title: 'Core R&D activity',
    contentKey: 'AFICoreActivityAppGuidanceText',
    deleteButtonTitle: 'Delete activity',
    deleteDialogBodyText: 'Are you sure you want to delete this core R&D activity?<br>Deleting the core R&D activity will remove it from any linked supporting R&D activities, which may need to be linked to another core R&D activity or deleted.',
    fields: {
        isToListPreviousCoreActivity,                       // Q.61
        name,                                               // Q.62
        describeCoreActivity,                               // Q.63
        projectId,                                          // Q.64
        isActivityExcludedFromCore,                         // Q.65
        activitiesPerformedBy,                              // Q.66
        companyName,                                        // Q.67
        isEntityConductingActivityConnectedOrAffiliated,    // Q.68
        isActivityRnDEntityLocatedOutsideAustralia,         // Q.69
        activityConductedBy,                                // Q.70
        subsidiaryConductAbn,                               // Q.71
        companyCountryOfResidence,                          // Q.72
        futureCoreActivity,                                 // Q.73
        duration,                                           // Q.74
        otherOrganisation,                                  // Q.75
        isActivityCoveredByIRDD,                            // Q.76
        determinationRelyingOn,                             // Q.77
        outcome,                                            // Q.78
        sourcesInvestigated,                                // Q.79
        competentProfesionalReason,                         // Q.80
        whyNoInvestigation,                                 // Q.81
        hypothesis,                                         // Q.82
        experiment,                                         // Q.83
        evaluation,                                         // Q.84
        conclusions,                                        // Q.85
        evidenceAvailable,                                  // Q.86
        evidenceAvailableOther,                             // Q.87
        purpose,                                            // Q.88
        newKnowledge,                                       // Q.89
        otherOrganisationName,                              // Q.90 or Q.91
        briefDescription,                                   // Q.90.1 or Q.91.1
        expenditure,                                        // Q.92
        totalAnticipatedExpendituresHeader,                 // Q.93
        expenditurePriorToYear1,                            // Q.93.1
        financialYear1EstimatedExpenditure,                 // Q.93.2
        financialYear2EstimatedExpenditure,                 // Q.93.3
        financialYear3EstimatedExpenditure,                 // Q.93.4
        financialYear3EstimatedExpenditurePost,             // Q.93.5
        totalExpenditure,                                   // Q.93.6

    },
    initialPageValues: {
        isToListPreviousCoreActivity: undefined,
        name: '',
        describeCoreActivity: '',
        projectId: '',
        projects: [],
        isActivityExcludedFromCore: undefined,
        activitiesPerformedBy: undefined,
        companyName: '',
        isEntityConductingActivityConnectedOrAffiliated: undefined,
        isActivityRnDEntityLocatedOutsideAustralia: undefined,
        activityConductedBy: undefined,
        subsidiaryConductAbn: undefined,
        companyCountryOfResidence: '',
        futureCoreActivity: undefined,
        duration: {
            endMonth: undefined,
            endYear: undefined,
            startMonth: undefined,
            startYear: undefined,
        },
        otherOrganisation: 'No',
        isActivityCoveredByIRDD: undefined,
        determinationRelyingOn: '',
        outcome: [],
        sourcesInvestigated: '',
        competentProfesionalReason: '',
        whyNoInvestigation: '',
        hypothesis: '',
        experiment: '',
        evaluation: '',
        conclusions: '',
        evidenceAvailable: [],
        evidenceAvailableOther: '',
        purpose: undefined,
        newKnowledge: '',
        otherOrganisationName: '',
        briefDescription: '',
        expenditure: '' as any,
        expenditurePriorToYear1: undefined,
        financialYear1EstimatedExpenditure: undefined,
        financialYear2EstimatedExpenditure: undefined,
        financialYear3EstimatedExpenditure: undefined,
        financialYear3EstimatedExpenditurePost: undefined,
        totalExpenditure: undefined,
    },
};

export default coreActivities;
